/* istanbul ignore file */
/**
 * API Service Singleton Wrapper
 * Wraps xivdyetools-core APIService with IndexedDB cache (F4)
 */

import {
  APIService as CoreAPIService,
  type ICacheBackend,
  UNIVERSALIS_API_BASE,
} from '@xivdyetools/core';
import type { PriceData, CachedData } from '@xivdyetools/types';
import { NoOpLogger } from '@xivdyetools/logger/library';
import { indexedDBService, STORES } from './indexeddb-service';
import { logger } from '@shared/logger';

/**
 * Get the Universalis API base URL
 * Uses proxy URL in production to avoid CORS issues with rate limit responses
 *
 * Order of precedence:
 * 1. VITE_UNIVERSALIS_PROXY_URL environment variable (for local development)
 * 2. Production proxy URL (for deployed app)
 * 3. Direct Universalis API URL (fallback - not recommended for browser)
 */
function getUniversalisBaseUrl(): string {
  // Check for environment variable (local dev)
  const envUrl = import.meta.env.VITE_UNIVERSALIS_PROXY_URL;
  if (envUrl) {
    logger.info(`Using Universalis proxy from env: ${envUrl}`);
    return envUrl;
  }

  // api-worker's absorbed Universalis routes (Monorepo 2.0 Tier 2 — formerly
  // the standalone proxy.xivdyetools.app worker). api-worker sets
  // `cors({ origin: '*' })`, so this answers every origin: production, beta,
  // and *.pages.dev alike. CORS headers are present even on errors like 429.
  //
  // HARD DEPENDENCY: these routes exist only once THIS branch's api-worker is
  // deployed to data.xivdyetools.app. Until that deploy, every market call is a
  // 404 and the Market Board is dead on any site built from this branch.
  //
  // The legacy `proxy.xivdyetools.app/api/v2` mount is NOT a usable stand-in
  // for beta: the old worker replies `Access-Control-Allow-Origin:
  // https://xivdyetools.app` to every caller regardless of Origin, so the
  // browser blocks it from any host but production. Measured 2026-08-10 — do
  // not "fix" a beta 404 by pointing back at it; that trades a 404 for a CORS
  // failure. Deploy api-worker instead.
  if (import.meta.env.PROD) {
    const proxyUrl = 'https://data.xivdyetools.app/universalis';
    logger.info(`Using production Universalis proxy: ${proxyUrl}`);
    return proxyUrl;
  }

  // Fallback to direct API (development without proxy)
  // Note: This may cause CORS issues if Universalis returns 429 errors
  logger.warn('Using direct Universalis API - CORS issues may occur on rate limit');
  return UNIVERSALIS_API_BASE;
}

/**
 * IndexedDB Cache Backend for browser environment (F4)
 * Uses IndexedDB for larger storage capacity and better performance
 * Falls back to memory-only if IndexedDB is unavailable
 */
export class IndexedDBCacheBackend implements ICacheBackend {
  private memoryCache: Map<string, CachedData<PriceData>> = new Map();
  private initPromise: Promise<void> | null = null;
  /**
   * Bumped by clear(). A hydration that began before a clear() (logout) —
   * counted from the database open, not just the read — must not put the
   * cleared prices back — FINDING-008 — and OPT-009's single snapshot read
   * would otherwise restore every one of them.
   */
  private clearGeneration = 0;

  /**
   * Initialize the backend asynchronously
   * BUG-004 FIX: Don't clear initPromise on error to prevent race conditions
   * with concurrent callers. Use reinitialize() for explicit retry.
   */
  async initialize(): Promise<void> {
    if (this.initPromise) return this.initPromise;

    this.initPromise = this.doInitialize();
    return this.initPromise;
  }

  /**
   * Perform the actual initialization
   */
  private async doInitialize(): Promise<void> {
    // OPT-009: the clear() guard is captured BEFORE the open. A clear() that
    // lands while the open is pending queues behind it after this
    // continuation, so a generation read once the open resolves would already
    // include that clear while the snapshot is still taken before it
    const generation = this.clearGeneration;
    const success = await indexedDBService.initialize();
    if (success) {
      await this.loadFromStorage(generation);
    }
  }

  get(key: string): CachedData<PriceData> | null {
    // Return from memory cache for sync operation
    return this.memoryCache.get(key) ?? null;
  }

  set(key: string, value: CachedData<PriceData>): void {
    // Update memory cache immediately
    this.memoryCache.set(key, value);

    // Persist to IndexedDB asynchronously with retry logic
    void this.persistWithRetry(key, value);
  }

  /**
   * Persist to IndexedDB with retry logic
   * If persistence consistently fails, remove from memory cache to avoid state mismatch
   */
  private async persistWithRetry(
    key: string,
    value: CachedData<PriceData>,
    attempt = 1,
    maxAttempts = 2
  ): Promise<void> {
    try {
      await indexedDBService.set(STORES.PRICE_CACHE, key, value);
    } catch (error) {
      if (attempt < maxAttempts) {
        logger.warn(
          `Failed to persist price data (attempt ${attempt}/${maxAttempts}), retrying:`,
          error
        );
        // Retry with exponential backoff
        await new Promise((resolve) => setTimeout(resolve, attempt * 100));
        await this.persistWithRetry(key, value, attempt + 1, maxAttempts);
      } else {
        logger.error(
          'Failed to persist price data after retries, removing from memory cache:',
          error
        );
        // Remove from memory cache to prevent false positive caching
        this.memoryCache.delete(key);
      }
    }
  }

  delete(key: string): void {
    this.memoryCache.delete(key);
    void indexedDBService.delete(STORES.PRICE_CACHE, key).catch((error) => {
      logger.warn('Failed to delete from IndexedDB:', error);
    });
  }

  clear(): void {
    this.clearGeneration++;
    this.memoryCache.clear();
    indexedDBService.clear(STORES.PRICE_CACHE).catch((error) => {
      logger.warn('Failed to clear IndexedDB cache:', error);
    });
  }

  keys(): string[] {
    return Array.from(this.memoryCache.keys());
  }

  /**
   * Load all cached data from IndexedDB into memory
   *
   * OPT-009: one readonly transaction for the whole store — it was keys() and
   * then a serial get() transaction per key. A price already in memory was
   * fetched while the read was in flight, so it is the newer one and stays.
   *
   * @param generation - `clearGeneration` as it was when initialization began
   */
  private async loadFromStorage(generation: number): Promise<void> {
    try {
      const entries = await indexedDBService.entries<CachedData<PriceData>>(STORES.PRICE_CACHE);
      if (generation !== this.clearGeneration) {
        logger.debug('Price cache cleared during hydration; stored entries discarded');
        return;
      }
      for (const [key, data] of entries) {
        if (data && !this.memoryCache.has(key)) {
          this.memoryCache.set(key, data);
        }
      }
      if (entries.length > 0) {
        logger.debug(`Loaded ${entries.length} price cache entries from IndexedDB`);
      }
    } catch (error) {
      logger.warn('Failed to load price cache from IndexedDB:', error);
    }
  }
}

/**
 * Web app singleton wrapper for APIService
 * Maintains backward compatibility with existing code using getInstance()
 */
export class APIService {
  private static instance: CoreAPIService | null = null;
  private static cacheBackend: IndexedDBCacheBackend | null = null;

  /**
   * Get singleton instance of APIService
   */
  static getInstance(): CoreAPIService {
    if (!APIService.instance) {
      // Use IndexedDB cache backend (F4)
      APIService.cacheBackend = new IndexedDBCacheBackend();

      // Get the appropriate base URL (proxy in production, direct in dev)
      const baseUrl = getUniversalisBaseUrl();

      // Create APIService with cache backend and proxy URL
      // Include logger to ensure options-based API is used (backwards compatible with older core versions)
      APIService.instance = new CoreAPIService({
        cacheBackend: APIService.cacheBackend,
        baseUrl,
        logger: NoOpLogger,
      });
      logger.info('✅ APIService initialized from xivdyetools-core with IndexedDB cache');

      // Initialize cache backend asynchronously (won't block)
      void APIService.cacheBackend.initialize().then(() => {
        const dbg = (logger as unknown as Record<string, unknown>).debug as
          ((...args: unknown[]) => void) | undefined;
        if (typeof dbg === 'function') {
          dbg('IndexedDB cache backend initialized');
        } else {
          // Fallback to console in test/mocked environments
          logger.info('IndexedDB cache backend initialized');
        }
      });
    }
    return APIService.instance;
  }

  /**
   * Reset singleton (for testing)
   */
  static resetInstance(): void {
    APIService.instance = null;
  }

  /**
   * Clear all cached price data
   */
  static async clearCache(): Promise<void> {
    return APIService.getInstance().clearCache();
  }
}

// Export singleton instance for direct use
export const apiService = APIService.getInstance();

// Re-export types
export type { PriceData };
